import { useState } from "react";
import { Download } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { rfqApi, procPdfApi } from "../../api";
import { ProcError, ProcField, ProcForm, ProcGrid, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { ProcStatusBadge } from "../../components/ProcStatusBadge";
import { useAsyncData, useMutation } from "../../hooks";

export default function RFQViewPage() {
  const { id } = useParams<{ id: string }>();
  const [downloading, setDownloading] = useState(false);

  const { data: rfq, loading, error, reload } = useAsyncData(
    () => rfqApi.get(Number(id)),
    [id],
  );

  const { mutate: doSend, loading: sending, error: sendErr } = useMutation(() =>
    rfqApi.send(Number(id)).then(() => reload()),
  );

  const { mutate: doClose, loading: closing, error: closeErr } = useMutation(() =>
    rfqApi.close(Number(id)).then(() => reload()),
  );

  async function downloadPdf() {
    if (!rfq) return;
    setDownloading(true);
    try {
      const blob = await procPdfApi.rfq(Number(id));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `rfq-${rfq.serialNo ?? id}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  }

  if (loading) return <ProcLoading />;
  if (error) return <ProcError message={error} />;
  if (!rfq) return null;

  const actionErr = sendErr || closeErr;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <Link to="/procurement/rfqs" className="text-sm text-emerald-700 hover:underline">&larr; Back to RFQs</Link>
        <button onClick={downloadPdf} disabled={downloading}
          className="flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          <Download className="h-4 w-4" />
          {downloading ? "Downloading…" : "Download PDF"}
        </button>
      </div>

      <ProcForm formNo="PROC-FRM-02" title="Request for Quotation" serialNo={rfq.serialNo} date={rfq.date}>
        <ProcSection title="RFQ Details">
          <ProcGrid>
            <ProcField label="Deadline" value={rfq.deadline ? new Date(rfq.deadline).toLocaleDateString("en-KE") : "—"} />
            <ProcField label="Submission Method" value={rfq.submissionMethod} />
            <ProcField label="Linked Requisition" value={rfq.requisitionSerialNo ?? (rfq.requisitionId ? String(rfq.requisitionId) : "—")} />
            <ProcField label="Status" value={<ProcStatusBadge value={rfq.status} />} />
          </ProcGrid>
        </ProcSection>

        <ProcSection title="Invited Suppliers">
          {rfq.suppliers && rfq.suppliers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Email Sent</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rfq.suppliers.map((s, i) => (
                    <tr key={s.id ?? i}>
                      <td className="px-3 py-2 font-medium text-slate-800">{s.supplierName ?? s.adHocName ?? "—"}</td>
                      <td className="px-3 py-2 text-slate-500">{s.supplierEmail ?? s.adHocEmail ?? "—"}</td>
                      <td className="px-3 py-2">
                        {s.supplierId != null ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Registered</span>
                        ) : (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Ad-hoc</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-500">
                        {s.emailSent ? `Sent${s.emailSentAt ? " " + new Date(s.emailSentAt).toLocaleString("en-KE") : ""}` : "Not yet"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <ProcGrid>
              <ProcField label="Supplier" value={rfq.toSupplier ?? "—"} />
              <ProcField label="Contact Person" value={rfq.contactPerson ?? "—"} />
            </ProcGrid>
          )}
        </ProcSection>

        <ProcSection title="Line Items">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-left">Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rfq.lines.map((l) => (
                  <tr key={l.id ?? l.lineOrder}>
                    <td className="px-3 py-2 text-slate-400">{l.lineOrder}</td>
                    <td className="px-3 py-2">{l.description}</td>
                    <td className="px-3 py-2 text-right">{l.quantity}</td>
                    <td className="px-3 py-2 text-slate-500">{l.remarks || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ProcSection>

        {(rfq.status === "DRAFT" || rfq.status === "SENT") && (
          <ProcSection title="Actions">
            {actionErr && <ProcError message={actionErr} />}
            <div className="flex flex-wrap gap-3">
              {rfq.status === "DRAFT" && (
                <button
                  type="button"
                  onClick={() => doSend()}
                  disabled={sending}
                  className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send RFQ"}
                </button>
              )}
              {rfq.status === "SENT" && (
                <button
                  type="button"
                  onClick={() => doClose()}
                  disabled={closing}
                  className="rounded-xl bg-slate-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-60"
                >
                  {closing ? "Closing…" : "Close RFQ"}
                </button>
              )}
            </div>
          </ProcSection>
        )}
      </ProcForm>
    </div>
  );
}
